import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../api/axios';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null); // 'admin'
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Logout
  const logout = useCallback(async () => {
    try {
      await authAPI.adminLogout();
    } catch (error) {
      // Ignore logout errors
    }

    localStorage.removeItem('accessToken');
    localStorage.removeItem('userType');
    localStorage.removeItem('user');

    setUser(null);
    setUserType(null);
    setIsAuthenticated(false);
  }, []);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('accessToken');
      const storedUserType = localStorage.getItem('userType');
      const storedUser = localStorage.getItem('user');

      if (token && storedUserType === 'admin' && storedUser) {
        try {
          setUserType('admin');
          setUser(JSON.parse(storedUser));
          setIsAuthenticated(true);

          // Verify token by fetching profile
          const response = await authAPI.getAdminProfile();
          const userData = response.data.user;
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
        } catch (error) {
          // Token invalid - clear state
          logout();
        }
      }
      setLoading(false);
    };

    initAuth();
  }, [logout]);

  // Admin login
  const loginAdmin = async (email, password) => {
    const response = await authAPI.adminLogin({ email, password });
    const { user, accessToken } = response.data;

    localStorage.setItem('accessToken', accessToken);
    localStorage.setItem('userType', 'admin');
    localStorage.setItem('user', JSON.stringify(user));

    setUser(user);
    setUserType('admin');
    setIsAuthenticated(true);

    return response.data;
  };

  // Update user profile in state
  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  // Check if user has admin role
  const isAdmin = () => {
    return userType === 'admin' && user?.roles?.includes('admin');
  };

  const value = {
    user,
    userType,
    loading,
    isAuthenticated,
    loginAdmin,
    logout,
    updateUser,
    isAdmin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
